import { defineStore } from 'pinia'
import { useTripBuilderStore } from '@/stores/trip-builder'
import type { FilterDescriptor } from '@progress/kendo-data-query'
import { FilterOperator } from '@progress/kendo-data-query'
import type { CountryInterface, RegionInterface } from '@/types/app'
import type { ServiceInterface, TagInterface } from '@/types/trips'
import type { SupplierInterface } from '@/types/suppliers'

export const useServiceManagerStore = defineStore('serviceManager', {
  state: () => ({
    context: {
      date: null as string | null,
    },
    filters: {
      servicePartial: null as string | null,
      supplierPartial: null as string | null,
      countries: [] as CountryInterface[],
      regions: [] as RegionInterface[],
      cities: [] as TagInterface[],
      types: [] as TagInterface[],
      suppliers: [] as SupplierInterface[],
      isArchived: false as boolean,
    },
  }),
  getters: {
    predicates(state) {
      // console.debug(state);
      const filters: Array<FilterDescriptor> = [];
      if ((state.filters.servicePartial?.length ?? 0) > 0) {
        filters.push({ field: 'service.name', operator: FilterOperator.Contains, value: state.filters.servicePartial })
      }

      if ((state.filters.supplierPartial?.length ?? 0) > 0) {
        filters.push({ field: 'supplier.name', operator: FilterOperator.Contains, value: state.filters.supplierPartial })
      }

      if (state.filters.countries.length > 0) {
        filters.push({ field: 'country.uuid', operator: FilterOperator.EqualTo, value: state.filters.countries.map(item => item.uuid) })
      }

      if (state.filters.regions.length > 0) {
        filters.push({ field: 'region.uuid', operator: FilterOperator.EqualTo, value: state.filters.regions.map(item => item.uuid) })
      }

      if (state.filters.cities.length > 0) {
        filters.push({ field: 'supplier.city', operator: 'matches', value: state.filters.cities.map(item => item.label) })
      }

      if (state.filters.types.length > 0) {
        filters.push({ field: 'type.uuid', operator: FilterOperator.EqualTo, value: state.filters.types.map(item => item.uuid) })
      }

      return filters;
    },
    servicePredicates(state) {
      // console.debug(state);
      const filters: Array<FilterDescriptor> = [];
      if ((state.filters.servicePartial?.length ?? 0) > 0) {
        filters.push({ field: 'service.name', operator: FilterOperator.Contains, value: state.filters.servicePartial })
      }

      if (state.filters.countries.length > 0) {
        filters.push({ field: 'country.uuid', operator: FilterOperator.EqualTo, value: state.filters.countries.map(item => item.uuid) })
      }

      if (state.filters.regions.length > 0) {
        filters.push({ field: 'region.uuid', operator: FilterOperator.EqualTo, value: state.filters.regions.map(item => item.uuid) })
      }

      if (state.filters.cities.length > 0) {
        filters.push({ field: 'service.city', operator: 'matches', value: state.filters.cities.map(item => item.label) })
      }

      if (state.filters.types.length > 0) {
        filters.push({ field: 'type.uuid', operator: FilterOperator.EqualTo, value: state.filters.types.map(item => item.uuid) })
      }

      if (state.filters.suppliers.length > 0) {
        filters.push({ field: 'supplier.uuid', operator: FilterOperator.EqualTo, value: state.filters.suppliers.map(item => item.uuid) })
      }

      if (state.filters.isArchived === true) {
        filters.push({ field: 'service.status', operator: FilterOperator.EqualTo, value: 'unavailable' })
      }
      else {
        filters.push({ field: 'service.status', operator: FilterOperator.EqualTo, value: 'available' })
      }

      return filters;
    },
  },
  actions: {
    matchedService(supplier?: SupplierInterface, service?: ServiceInterface, types?: Array<TagInterface>): boolean {
      if (!supplier) return false;
      if (!service) return false;
      const predicates = this.predicates;

      let matched = predicates.length === 0;
      // console.debug("ServiceManagerStore | matchedService | ", matched);
      predicates.forEach((filter: FilterDescriptor) => {
        console.debug("ServiceManagerStore | matchedService | predicates | ", filter);
        let filterMatch = false;
        switch (filter.field) {
          case 'supplier.name':
            // console.debug(service.name?.indexOf(filter.value), filter.value, service.name);
            filterMatch = (supplier.name?.indexOf(filter.value) ?? null) !== null;
            break;

          case 'supplier.uuid':
            // console.debug(service.name?.indexOf(filter.value), filter.value, service.name);
            filterMatch = filter.value.includes(supplier.uuid);
            break;

          case 'service.name':
            // console.debug(service.name?.indexOf(filter.value), filter.value, service.name);
            filterMatch = (service.name?.indexOf(filter.value) ?? null) !== null;
            break;

          case 'country.uuid':
            // console.debug(filter.value.includes(supplier.country?.uuid ?? ""), filter.value, supplier.country);
            filterMatch = filter.value.includes(supplier.country?.uuid ?? "");
            break;

          case 'region.uuid':
            // console.debug(filter.value.includes(supplier?.region?.uuid ?? ""), filter.value, supplier.region);
            filterMatch = filter.value.includes(supplier?.region?.uuid ?? "");
            break;

          case 'supplier.city':
            // console.debug(supplier.city?.toLowerCase().indexOf(filter.value.toLowerCase()), filter.value, supplier.city);
            //console.debug(filter);
            filterMatch = filter.value.includes(supplier.city?.toLowerCase() ?? "");
            break;

          case 'type.uuid':
            console.debug(filter.value.includes(service.type?.uuid ?? ""), filter.value, service.type);
            filterMatch = ((!types) || types.length === 0) || filter.value.includes(service.type?.uuid ?? "")
            break;
        }

        // console.debug(matched, filterMatch, service, filter);
        matched = matched || filterMatch;
      });

      return matched;
    },
    hasFilter(type: TagInterface) {
      return this.filters.types.indexOf(type) >= 0;
    },
    clearTooltips() {
      useTripBuilderStore().clearTooltips();
    },
    async resetOptions() {
      console.info("ServiceManager | resetOptions")

      await this.$patch({
        filters: {
          servicePartial: null,
          supplierPartial: null,
          countries: [],
          regions: [],
          cities: [],
          types: [],
          suppliers: [],
          isArchived: false,
        },
      })
    },
  },
})
